import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { TOKEN_EXPIRY_OPTIONS } from "@/config/mcpClients";
import { THEME } from "@/config/theme";
import ConfirmDialog from "@/components/sidebar/ConfirmDialog";
import KeyList from "@/components/mcp/KeyList";
import SetupSnippet from "@/components/mcp/SetupSnippet";
import TokenDialog from "@/components/mcp/TokenDialog";
import {
  createMcpKey,
  listMcpKeys,
  revokeMcpKey,
  type ApiMcpKey,
  type ApiMcpKeyCreated,
} from "@/services/mcpKeyService";

export default function McpAccess() {
  const { getToken } = useAuth();
  // Held in a ref so `refresh` has a stable identity. Depending on getToken
  // directly makes refresh change whenever Clerk hands back a new function
  // reference, which re-fires the mount effect and loops fetching forever.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const [keys, setKeys] = useState<ApiMcpKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(TOKEN_EXPIRY_OPTIONS[0]!.days);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiMcpKeyCreated | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiMcpKey | null>(null);

  // Why the submit button is inert, or null when it is ready. Doubles as the
  // hover hint, so the reason is always visible rather than implied by a cursor.
  const blockedReason = creating
    ? "Creating…"
    : newName.trim()
      ? null
      : "Type token name";

  const refresh = useCallback(async () => {
    try {
      setKeys(await listMcpKeys(getTokenRef.current));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tokens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const key = await createMcpKey(getTokenRef.current, newName.trim(), expiresInDays);
      setCreated(key);
      setNewName("");
      setError(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create token");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    const key = pendingRevoke;
    setPendingRevoke(null);
    if (!key) return;
    try {
      await revokeMcpKey(getTokenRef.current, key.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke token");
    }
  }

  return (
    // h-screen + overflow-y-auto, not min-h-screen: index.css sets
    // `overflow: hidden` on html/body/#root for the canvas, so a min-h-screen
    // page taller than the viewport is silently unscrollable and its lower
    // content is unreachable. This page owns its own scroll container.
    //
    // [color-scheme:dark] makes the browser paint its own chrome — scrollbars
    // and the native <select> dropdowns — dark instead of white. Scoped here
    // rather than on :root so it cannot surprise the canvas.
    <div
      className={`h-screen overflow-y-auto [color-scheme:dark] ${THEME.scrollbar.dark} ${THEME.panel.text} bg-canvas-bg`}
    >
      <div className="mx-auto max-w-3xl px-6 py-10 flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Link
            to="/"
            className={`inline-flex items-center gap-1.5 ${THEME.mcpPage.hint} hover:text-zinc-200 w-fit transition-colors`}
          >
            <ArrowLeft size={14} />
            Back to canvas
          </Link>
          <h1 className={THEME.mcpPage.title}>MCP Access</h1>
          <p className={THEME.mcpPage.intro}>
            Connect Taitora to Claude and other MCP clients. Create a token, then
            paste the setup snippet into your client — it will be able to build
            forges in your workspace.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="text-[14px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <section className="flex flex-col gap-3">
          <h2 className={`${THEME.pageCard.label} ${THEME.mcpPage.sectionLabel}`}>
            Your tokens
          </h2>

          <form onSubmit={handleCreate} className="flex items-center gap-2 flex-wrap">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Type token name"
              aria-label="Token name"
              maxLength={64}
              className={`${THEME.panel.input} ${THEME.mcpPage.control} rounded-lg px-3 py-2 flex-1 min-w-[180px]`}
            />
            <select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              aria-label="Token expiry"
              className={`${THEME.panel.input} ${THEME.mcpPage.control} rounded-lg px-2.5 py-2`}
            >
              {TOKEN_EXPIRY_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>
                  {o.label}
                </option>
              ))}
            </select>
            {/*
              aria-disabled rather than the `disabled` attribute: a disabled
              button receives no pointer events in most browsers, so its title
              tooltip never appears and the user just gets a no-entry cursor
              with no explanation. Keeping it enabled lets the hint show; the
              submit handler guards the actual action.
            */}
            <button
              type="submit"
              aria-disabled={blockedReason !== null}
              title={blockedReason ?? undefined}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 ${THEME.mcpPage.control} rounded-lg ${
                blockedReason ? THEME.button.primaryIdle : THEME.button.primary
              } transition-colors`}
            >
              <Plus size={15} />
              {creating ? "Creating…" : "New token"}
            </button>
          </form>

          {loading ? (
            <p className={`${THEME.mcpPage.hint} py-6 text-center`}>Loading…</p>
          ) : (
            <KeyList keys={keys} onRevoke={setPendingRevoke} />
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className={`${THEME.pageCard.label} ${THEME.mcpPage.sectionLabel}`}>Setup</h2>
          <SetupSnippet />
        </section>
      </div>

      {created && (
        <TokenDialog
          token={created.token}
          name={created.name}
          onClose={() => setCreated(null)}
        />
      )}

      {pendingRevoke && (
        <ConfirmDialog
          title="Revoke token"
          message={`"${pendingRevoke.name}" will stop working immediately. Any client using it will lose access. This cannot be undone.`}
          confirmLabel="Revoke"
          onConfirm={() => void handleRevoke()}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </div>
  );
}
